import { useState, useEffect, useRef } from "react";
import { Check, X, Lock, ChevronDown, AlertTriangle, Sparkles } from "lucide-react";

const PLATFORMS = {
  ChatGPT: { color: "#10A37F", bgLight: "#ecfdf5", letter: "G", label: "ChatGPT" },
  "Claude AI": { color: "#D97706", bgLight: "#fffbeb", letter: "C", label: "Claude AI" },
  "Google AI": { color: "#4285F4", bgLight: "#eff6ff", letter: "G", label: "Google AI" },
  Perplexity: { color: "#1a1a2e", bgLight: "#f1f5f9", letter: "P", label: "Perplexity" },
  "Bing AI": { color: "#0078D4", bgLight: "#eff6ff", letter: "B", label: "Bing AI" },
};

function AICard({ platform, status, snippet, queryUsed, businessName, index, isLocked, mentionContext, competitors }) {
  const [flipped, setFlipped] = useState(false);
  const ref = useRef(null);
  const config = PLATFORMS[platform] || PLATFORMS.Perplexity;
  const mentioned = status === "found";
  const notFound = status === "not_found";

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setFlipped(true); obs.disconnect(); }
    }, { threshold: 0.2 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const highlightName = (text) => {
    if (!text || !businessName) return text;
    const regex = new RegExp(`(${businessName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, "gi");
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part)
        ? <mark key={i} style={{ background: "#bbf7d0", padding: "1px 4px", fontWeight: 700, color: "#166534" }}>{part}</mark>
        : part
    );
  };

  const borderColor = isLocked ? "#CBD5E1" : mentioned ? "#22c55e" : "#E11D48";

  return (
    <div
      ref={ref}
      data-testid={`ai-card-${platform.toLowerCase().replace(/\s/g, "-")}`}
      style={{ width: "100%" }}
    >
      <div style={{
        background: "#fff", border: "1px solid #E2E8F0",
        borderTop: isLocked ? "2px dashed #CBD5E1" : `3px solid ${borderColor}`,
        padding: "28px 18px 22px", textAlign: "center",
        display: "flex", flexDirection: "column", alignItems: "center", minHeight: 290,
        opacity: flipped ? 1 : 0,
        transform: flipped ? "rotateY(0)" : "rotateY(90deg)",
        transition: `opacity 0.5s ease ${index * 150}ms, transform 0.6s ease ${index * 150}ms`,
      }}>
        {/* Platform badge */}
        <div style={{
          width: 40, height: 40,
          background: config.color, display: "flex", alignItems: "center", justifyContent: "center",
          marginBottom: 10,
        }}>
          <span style={{ color: "#fff", fontWeight: 800, fontSize: 18, fontFamily: "'Outfit',sans-serif" }}>{config.letter}</span>
        </div>
        <p style={{ fontSize: 14, fontWeight: 700, color: "#0F172A", marginBottom: 14, fontFamily: "'IBM Plex Sans',sans-serif" }}>{config.label}</p>

        {/* Status icon */}
        {isLocked ? (
          <div style={{ width: 44, height: 44, background: "#F1F5F9", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 10 }}>
            <Lock size={20} style={{ color: "#94A3B8" }} />
          </div>
        ) : mentioned ? (
          <div style={{ width: 44, height: 44, background: "#DCFCE7", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 10 }}>
            <Check size={22} style={{ color: "#16a34a" }} />
          </div>
        ) : (
          <div style={{ width: 44, height: 44, background: "#FEF2F2", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 10 }}>
            <X size={22} style={{ color: "#E11D48" }} />
          </div>
        )}

        {/* Status text */}
        <p style={{
          fontSize: 14, fontWeight: 700, marginBottom: 10, fontFamily: "'IBM Plex Sans',sans-serif",
          color: isLocked ? "#94A3B8" : mentioned ? "#16a34a" : "#E11D48",
        }}>
          {isLocked ? `Check ${config.label}` : mentioned ? "Recommended!" : "Not Found"}
        </p>

        {/* Body text */}
        <div style={{ fontSize: 12, color: "#64748B", lineHeight: 1.65, margin: 0, fontFamily: "'IBM Plex Sans',sans-serif", textAlign: "left", width: "100%" }}>
          {isLocked ? (
            <p style={{ textAlign: "center", margin: 0 }}>Unlock to see if {config.label} recommends your business</p>
          ) : mentioned && mentionContext ? (
            <div>
              <p style={{ fontSize: 11, color: "#94A3B8", marginBottom: 4, fontWeight: 600 }}>{config.label} said:</p>
              <p style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", padding: "8px 10px", margin: 0, fontSize: 12, lineHeight: 1.6, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                {highlightName(mentionContext)}
              </p>
            </div>
          ) : notFound && competitors?.length > 0 ? (
            <div>
              <p style={{ fontSize: 11, color: "#94A3B8", marginBottom: 4, fontWeight: 600 }}>{config.label} recommends instead:</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {competitors.slice(0, 3).map((c, i) => (
                  <span key={i} style={{ fontSize: 12, color: "#E11D48", fontWeight: 600 }}>
                    {i + 1}. {c}
                  </span>
                ))}
              </div>
            </div>
          ) : notFound ? (
            <p style={{ textAlign: "center", margin: 0 }}>Not mentioned when customers ask for {config.label} recommendations.</p>
          ) : (
            <p style={{ textAlign: "center", margin: 0 }}>Could not check — try again later.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function AlertBanner({ score, total }) {
  if (score >= total && total > 0) {
    return (
      <div data-testid="ai-alert-banner" style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", padding: "16px 24px", textAlign: "center" }}>
        <p style={{ fontSize: 15, fontWeight: 700, color: "#16a34a", fontFamily: "'Outfit',sans-serif" }}>Strong AI presence across all platforms</p>
      </div>
    );
  }
  if (score >= 1) {
    return (
      <div data-testid="ai-alert-banner" style={{ background: "#FFFBEB", border: "1px solid #FDE68A", padding: "16px 24px", textAlign: "center" }}>
        <p style={{ fontSize: 15, fontWeight: 700, color: "#D97706", marginBottom: 4, fontFamily: "'Outfit',sans-serif" }}>Partial AI visibility</p>
        <p style={{ fontSize: 13, color: "#92400E", fontFamily: "'IBM Plex Sans',sans-serif" }}>{score} of {total} AI platforms recommend your business.</p>
      </div>
    );
  }
  return (
    <div data-testid="ai-alert-banner" style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderLeft: "4px solid #E11D48", padding: "20px 24px" }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <AlertTriangle size={20} style={{ color: "#E11D48", flexShrink: 0, marginTop: 2 }} />
        <div>
          <p style={{ fontSize: 16, fontWeight: 700, color: "#9F1239", marginBottom: 6, fontFamily: "'Outfit',sans-serif" }}>AI Search Blind Spot</p>
          <p style={{ fontSize: 13, color: "#9F1239", lineHeight: 1.7, margin: 0, fontFamily: "'IBM Plex Sans',sans-serif" }}>
            0 of {total} AI platforms recommend your business. Your competitors ARE being recommended instead. This gap grows daily as AI search usage increases.
          </p>
        </div>
      </div>
    </div>
  );
}

function AIResponseDetails({ platforms }) {
  const [open, setOpen] = useState(false);
  const realPlatforms = platforms.filter(p => p.response_snippet && p.source === "live");
  if (realPlatforms.length === 0) return null;

  return (
    <div style={{ marginTop: 16 }}>
      <button data-testid="ai-details-toggle" onClick={() => setOpen(!open)}
        style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#64748B", padding: "8px 0", fontFamily: "'IBM Plex Sans',sans-serif" }}>
        <ChevronDown size={14} style={{ transform: open ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.2s" }} />
        See full AI responses
      </button>
      {open && (
        <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", padding: "20px 24px", marginTop: 8 }}>
          {realPlatforms.map((p, i) => (
            <div key={i} style={{ marginBottom: i < realPlatforms.length - 1 ? 20 : 0 }}>
              {p.query_used && (
                <p style={{ fontSize: 12, color: "#94A3B8", fontStyle: "italic", marginBottom: 6, fontFamily: "'IBM Plex Sans',sans-serif" }}>
                  We asked: &ldquo;{p.query_used}&rdquo;
                </p>
              )}
              <p style={{ fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 4, fontFamily: "'IBM Plex Sans',sans-serif" }}>{p.platform} said:</p>
              <p style={{
                fontSize: 12, color: "#475569", lineHeight: 1.7,
                background: "#fff", padding: "10px 14px", border: "1px solid #E2E8F0",
                display: "-webkit-box", WebkitLineClamp: 6, WebkitBoxOrient: "vertical", overflow: "hidden",
                fontFamily: "'IBM Plex Sans',sans-serif", whiteSpace: "pre-wrap",
              }}>
                {p.response_snippet}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AIVisibilityCards({ ai_visibility, business, hasAccess }) {
  const aiVis = ai_visibility || {};
  const platforms = aiVis.platforms || [];
  const score = aiVis.score ?? 0;
  const total = aiVis.total ?? 3;
  const businessName = business?.name || "";

  const displayPlatforms = platforms.map(p => {
    if (hasAccess && p.status === "locked") {
      return { ...p, status: "not_checked" };
    }
    return p;
  });

  return (
    <section id="ai" data-testid="ai-visibility-section" style={{ padding: "20px 24px" }}>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 12, marginBottom: 20 }}>
        {displayPlatforms.map((p, i) => (
          <AICard
            key={p.platform}
            platform={p.platform}
            status={p.status}
            snippet={p.response_snippet}
            queryUsed={p.query_used}
            businessName={businessName}
            index={i}
            isLocked={p.status === "locked"}
            mentionContext={p.mention_context}
            competitors={p.competitors_mentioned}
          />
        ))}
      </div>

      <AlertBanner score={score} total={total} />
      <AIResponseDetails platforms={platforms} />
    </section>
  );
}
